import { Link, useNavigate } from 'react-router-dom';

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@src/design-system/accordion';

import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@src/design-system/sidebar';
import { useIsActiveUrl } from './hooks';

import type { TNavLink } from './types';
import { NavLinkBadge } from './nav-link-badge';

export const NavLinkWithChildren = ({
  item,
  isSectionExpanded,
}: {
  item: TNavLink;
  isSectionExpanded: boolean;
}) => {
  const isActiveUrl = useIsActiveUrl();
  const navigate = useNavigate();
  const parentUrlActive =
    'url' in item && item.url ? isActiveUrl(item.url) : false;
  const isMenuSubItemActive =
    parentUrlActive ||
    !!item.items?.some((subItem) => isActiveUrl(subItem.url));
  const { setOpen, open } = useSidebar();

  const handleTriggerClick = () => {
    if (!open) setOpen(true);
    // Only navigate to parent URL when opening the section (e.g. from Follow Ups), not when closing
    if (!isSectionExpanded && 'url' in item && item.url) navigate(item.url);
  };

  return (
    <AccordionItem
      asChild
      key={item.title}
      value={item.title}
    >
      <SidebarMenuItem>
        <SidebarMenuButton
          isActive={!!isMenuSubItemActive}
          onClick={() => {
            if (open) return;
            setOpen(true);
          }}
          className='group-has-data-[state=open]/menu-item:bg-white/5'
          tooltip={item.title}
        >
          <AccordionTrigger onClick={handleTriggerClick}>
            <div className='flex gap-2 [&_svg]:size-5 shrink-0 '>
              {isMenuSubItemActive && item.icon ? (
                <item.icon variant='Bold' />
              ) : item.icon ? (
                <item.icon />
              ) : null}

              <span>{item.title}</span>
              <NavLinkBadge item={item} />
            </div>
          </AccordionTrigger>
        </SidebarMenuButton>

        <AccordionContent
          asChild
          className='pb-0'
        >
          <SidebarMenuSub>
            {item.items?.map((subItem) => {
              if (!subItem.isAllowed) return null;
              return (
                <NavLinkChild
                  key={subItem.title}
                  item={subItem}
                />
              );
            })}
          </SidebarMenuSub>
        </AccordionContent>
      </SidebarMenuItem>
    </AccordionItem>
  );
};

const NavLinkChild = ({ item }: { item: TNavLink }) => {
  const isActiveUrl = useIsActiveUrl();
  const isMenuSubItemActive = isActiveUrl(item.url);

  return (
    <SidebarMenuSubItem key={item.title}>
      <SidebarMenuSubButton
        isActive={isMenuSubItemActive}
        asChild
      >
        <Link to={item.url || '/'}>
          {item.title}
          <NavLinkBadge item={item} />
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
};
